import { Check } from 'lucide-react';
import { localeTagFromLang } from '../../utils/bookingLocaleDisplay';
import { CalendarPickerButton } from './CalendarPickerButton';
import './BookingComponents.css';
import './booking-brand.css';

export interface ConfirmedLineVM {
  serviceName: string;
  slotStartTime?: string | null;
  slotEndTime?: string | null;
  durationMinutes?: number | null;
  resourceName?: string | null;
}

interface BookingConfirmationProps {
  lines: ConfirmedLineVM[];
  total?: number;
  currency?: string | null;
  /** 'postpaid' se muestra como "A pagar en el centro"; el resto como "Pagado". */
  paymentMode?: string | null;
  customerEmail?: string | null;
  language?: string;
  tenantId?: string;
  t: (key: string, options?: Record<string, string | number>) => string;
  onSeeBookings: () => void;
  onBackHome: () => void;
}

function money(cents: number, currency: string | null | undefined, locale: string): string {
  const amount = cents / 100;
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: (currency || 'EUR').toUpperCase(),
    }).format(amount);
  } catch {
    return `${amount.toFixed(2)} ${currency ?? ''}`.trim();
  }
}

/**
 * Pantalla final del flujo de reserva. Cada línea confirmada se lee como una fila de agenda —
 * raíl de fecha a la izquierda, servicio y hora a la derecha — porque un carrito puede llevar
 * varias citas en días distintos y un único bloque de texto las aplasta en una sola cosa.
 *
 * No se muestra ningún "código de reserva": la plataforma no genera uno y un identificador
 * inventado no serviría para preguntar en recepción.
 */
export function BookingConfirmation({
  lines,
  total,
  currency,
  paymentMode,
  customerEmail,
  language = 'es',
  tenantId,
  t,
  onSeeBookings,
  onBackHome,
}: BookingConfirmationProps) {
  const locale = localeTagFromLang(language);
  const hasTotal = typeof total === 'number' && total > 0;
  const isPostpaid = paymentMode === 'postpaid';
  // Solo se ofrece el calendario cuando hay una sola cita con inicio y fin: un .ics por pantalla.
  const single = lines.length === 1 ? lines[0] : null;
  const canAddToCalendar = Boolean(single?.slotStartTime && single?.slotEndTime);

  const railParts = (iso?: string | null) => {
    if (!iso) return null;
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    try {
      return {
        weekday: new Intl.DateTimeFormat(locale, { weekday: 'short' }).format(d).replace('.', ''),
        day: new Intl.DateTimeFormat(locale, { day: 'numeric' }).format(d),
        month: new Intl.DateTimeFormat(locale, { month: 'short' }).format(d).replace('.', ''),
        time: new Intl.DateTimeFormat(locale, { hour: '2-digit', minute: '2-digit', hour12: false }).format(d),
      };
    } catch {
      return null;
    }
  };

  return (
    <div className="booking-step bk-step bk-confirm">
      <div className="bk-confirm-head">
        <span className="bk-confirm-icon" aria-hidden>
          <Check size={22} strokeWidth={2.4} />
        </span>
        <h2 className="bk-step-title">{t('booking.confirmed_title')}</h2>
        <p className="bk-confirm-lead">
          {customerEmail
            ? t('booking.confirmed_lead_email', { email: customerEmail })
            : t('booking.confirmed_lead')}
        </p>
      </div>

      <div className="bk-review-card">
        {lines.map((line, idx) => {
          const rail = railParts(line.slotStartTime);
          const meta = [
            rail?.time,
            line.durationMinutes ? t('booking.duration_min', { minutes: line.durationMinutes }) : '',
            line.resourceName ? t('booking.with_professional', { name: line.resourceName }) : '',
          ]
            .filter(Boolean)
            .join(' · ');
          return (
            <div key={`${line.serviceName}-${idx}`} className="bk-confirm-line">
              {rail ? (
                <div className="bk-confirm-rail" aria-hidden>
                  <span className="bk-confirm-rail-weekday">{rail.weekday}</span>
                  <span className="bk-confirm-rail-day">{rail.day}</span>
                  <span className="bk-confirm-rail-month">{rail.month}</span>
                </div>
              ) : null}
              <div className="bk-confirm-line-text">
                <span className="bk-confirm-line-name">{line.serviceName}</span>
                {meta ? <span className="bk-confirm-line-meta">{meta}</span> : null}
              </div>
            </div>
          );
        })}

        {hasTotal ? (
          <div className="bk-confirm-row">
            <span>{isPostpaid ? t('booking.confirmed_to_pay_onsite') : t('booking.confirmed_paid')}</span>
            <strong>{money(total!, currency, locale)}</strong>
          </div>
        ) : null}
      </div>

      {canAddToCalendar && single ? (
        <div className="bk-confirm-calendar">
          <CalendarPickerButton
            uid={`cart-${single.slotStartTime}`}
            serviceName={single.serviceName}
            slotStart={single.slotStartTime!}
            slotEnd={single.slotEndTime!}
            filename={`reserva-${single.slotStartTime?.slice(0, 10)}.ics`}
            label={t('payment.result.add_to_calendar')}
            labelGoogle="Google Calendar"
            labelOther={t('payment.result.other_calendar')}
            tenantId={tenantId}
          />
        </div>
      ) : null}

      <div className="bk-review-footer">
        <button type="button" className="bk-confirm-secondary" onClick={onSeeBookings}>
          {t('booking.my_bookings')}
        </button>
        <button type="button" className="bk-confirm-ghost" onClick={onBackHome}>
          {t('booking.back_to_home')}
        </button>
      </div>
    </div>
  );
}
